import { useConfirmation } from '@/hooks/UseConfirmation'
import { TRASH_RETENTION_DAYS } from '@/constants/Trash'
import type { ConceptType } from '../Types'

interface UsePlaybookConfirmationsParams {
	onDeletePlay: (playId: string) => Promise<void>
	onPermanentDeletePlays: (playIds: string[]) => Promise<void>
	onDeleteConcept: (id: string) => Promise<void>
	onDeleteFormation: (id: string) => Promise<void>
}

/**
 * Hook for managing PlaybookEditor confirmation dialogs
 *
 * Consolidates all confirmation dialog logic into a single hook
 * to reduce boilerplate in the main component.
 */
export function usePlaybookConfirmations({
	onDeletePlay,
	onPermanentDeletePlays,
	onDeleteConcept,
	onDeleteFormation,
}: UsePlaybookConfirmationsParams) {
	const deletePlayConfirm = useConfirmation<string>({
		title: 'Delete Play',
		description: `This play will be moved to trash where it can be restored within ${TRASH_RETENTION_DAYS} days.`,
		confirmText: 'Delete',
		variant: 'destructive',
		onConfirm: onDeletePlay,
	})

	const permanentDeleteConfirm = useConfirmation<string[]>({
		title: 'Permanently Delete',
		description: (playIds) =>
			`Are you sure you want to permanently delete ${playIds.length} play${playIds.length === 1 ? '' : 's'}? This action cannot be undone.`,
		confirmText: 'Permanently Delete',
		variant: 'destructive',
		onConfirm: onPermanentDeletePlays,
	})

	const deleteConceptConfirm = useConfirmation<{ id: string; type: ConceptType }>({
		title: 'Delete Item',
		description: (item) => `Are you sure you want to delete this ${item.type}? This action cannot be undone.`,
		confirmText: 'Delete',
		variant: 'destructive',
		onConfirm: async ({ id, type }) => {
			if (type === 'concept') await onDeleteConcept(id)
			else if (type === 'formation') await onDeleteFormation(id)
		},
	})

	return {
		deletePlayConfirm,
		permanentDeleteConfirm,
		deleteConceptConfirm,
	}
}
